import type { Server } from "./types";

/**
 * Filters servers by search term (case-insensitive)
 * Searches through server ID and URI fields
 */
const filterServersBySearch = (
  servers: Server[],
  searchTerm: string
): Server[] => {
  if (!searchTerm.trim()) {
    return servers;
  }

  const normalizedTerm = searchTerm.toLowerCase().trim();
  return servers.filter((server) => {
    const uri = server.type === "http" ? server.http?.uri : server.mcp?.uri;
    return (
      server.id.toLowerCase().includes(normalizedTerm) ||
      uri?.toLowerCase().includes(normalizedTerm)
    );
  });
};

/**
 * Comprehensive filter function for servers
 * Includes name search, ID search, type filter, and status filter
 */
export const filterServers = (
  servers: Server[],
  filters: {
    nameSearch?: string;
    serverTypes?: string[];
    statuses?: string[];
  }
): Server[] => {
  let filtered = servers;

  // Filter by name search (searches ID and URI)
  if (filters.nameSearch?.trim()) {
    filtered = filterServersBySearch(filtered, filters.nameSearch);
  }

  // Filter by server types
  if (filters.serverTypes && filters.serverTypes.length > 0) {
    filtered = filtered.filter((server) =>
      filters.serverTypes?.some((type) => server.type === type)
    );
  }

  // Filter by status
  if (filters.statuses && filters.statuses.length > 0) {
    filtered = filtered.filter((server) => {
      const isEnabled = server.enabled;
      return filters.statuses?.includes(isEnabled.toString());
    });
  }

  return filtered;
};

/**
 * Groups servers by their binding type (project, tenant, static)
 */
export const groupServersByBinding = (servers: Server[]) => ({
  project: servers.filter((server) => server.binding.type === "project"),
  tenant: servers.filter((server) => server.binding.type === "tenant"),
  static: servers.filter((server) => server.binding.type === "static"),
});
